import '../../App.css';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Stage, Layer, Group } from 'react-konva';
import { useCanvas, useLocation, useGestures, useGetPosters } from '../../hooks';
import Poster from '../poster/Poster.js';
import Background from './Background.js'
import { SubmitPosterModal } from '../submitPosterModal'
import { LocationFetchScreen } from '../locationFetchScreen/LocationFetchScreen';
import { isMobile, gpsToCanvasCoords, distributePosters, calculatePixelsPerHundredFeet } from '../../utils';
import DistanceCircles from './DistanceCircles';

const InfiniteCanvas = () => {
  const {
    posters,
    setPosters
  } = useGetPosters();

  const {
    zipcode,
    locationFetchMessage,
    loading,
    error,
    getLocation,
    handleZipcodeSubmit,
    setLoading,
    setError
  } = useLocation();

  const {
    stageRef,
    scaleRef,
    isAnimatingRef,
    positionRef,
    stageSize,
    position,
    setScale,
    updatePositionRef,
    syncPositionState,
    updatePositionState,
    updatePositionRefOnly,
    handleResize,
    cleanup,
  } = useCanvas();

  const {
    handleWheel,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleDragStart,
    handleDragEnd,
    handlePosterTouchStart,
    handlePosterTouchEnd,
    highestZIndex,
    lastTouchedPosterId,
    cleanup: cleanupGestures,
    handleAnimationEnd,
    handleStageClick
  } = useGestures({
    stageRef,
    scaleRef,
    positionRef,
    isAnimatingRef,
    updatePositionRef,
    updatePositionRefOnly,
    updatePositionState,
    syncPositionState,
    setScale
  });
  //Set all initial states
  const [showHelp, setShowHelp] = useState(false);
  const [isInitialZooming, setIsInitialZooming] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isGetPosterRequestSent, setIsGetPosterRequestSent] = useState(false);
  const [isLocationSet, setIsLocationSet] = useState(false);
  const [location, setLocation] = useState();
  const [postersLoadedCount, setPostersLoadedCount] = useState(0);


  //Set all initial refs
  const inertiaAnimationRef = useRef(null);
  const zoomEndTimeoutRef = useRef(null);
  const initialAnimationRef = useRef(null);
  const isFirstRenderRef = useRef(true);
  const isInitializingRef = useRef(false);
  const isDomReadyRef = useRef(false);
  const pixelsPerHundredFeet = useRef(0);
  const pixelsPerMile = useRef(0);
  const posterCount = useRef(0);


  const searchParams = new URLSearchParams(window.location.search);

  const onPosterLoaded = useCallback(() => {
    setPostersLoadedCount(prevCount => {
      const newCount = prevCount + 1;
      //console.log(newCount);
      return newCount;
    });
  }, []);

  // Define isShareMode based on URL parameters
  const isShareMode = searchParams.has('lat') &&
    searchParams.has('long') &&
    searchParams.has('posterId');

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);


  const initializeLocation = useCallback(async () => {
    try {
      console.log('Requesting Location');
      // Get URL parameters
      const urlLat = searchParams.get('lat');
      const urlLong = searchParams.get('long');
      // Pass share mode info to getLocation
      const location = await getLocation(isShareMode, urlLat, urlLong);
      setLocation(location);
      setIsLocationSet(true);
      console.log("Location Received")
    } catch (err) {
      setError(err.message);
    }
  }, [searchParams, getLocation, isShareMode, setLocation, setIsLocationSet, setError])

  const initializePosters = useCallback(async () => {
    try {
      console.log('REST Get All Posters')
      const response = await fetch(`${process.env.REACT_APP_API_URL}/posters/${location.latitude}/${location.longitude}`)
      //const response = await fetch(`/data/posters.json`)
      setIsGetPosterRequestSent(true);
      if (!response.ok) {
        throw new Error('HTTP ERROR: ', response.status);
      }
      const postersData = await response.json();
      setPosters(postersData);
      console.log(postersData);
    } catch (err) {
      setError(err.message);
    }
  }, [location, setPosters, setIsGetPosterRequestSent, setError])
  
  const performInitialZoomAnimation = useCallback(() => {
    if (!stageRef.current || !isFirstRenderRef.current || isInitializingRef.current || !isDomReadyRef.current) {
      return;
    }

    isInitializingRef.current = true;
    isAnimatingRef.current = true;

    const startScale = isMobile() ? .1 : .2;
    const endScale = isMobile() ? 1.5 : 2.5;
    const duration = 3000;
    const pauseDuration = 1000;

    if (initialAnimationRef.current) {
      cancelAnimationFrame(initialAnimationRef.current);
      initialAnimationRef.current = null;
    }
    setScale(startScale);
    stageRef.current.scale({ x: startScale, y: startScale });
    stageRef.current.batchDraw();

    setTimeout(() => {
      let animationStartTime = null;

      const animate = (currentTime) => {
        if (!animationStartTime) {
          animationStartTime = currentTime;
          initialAnimationRef.current = requestAnimationFrame(animate);
          return;
        }

        const elapsed = currentTime - animationStartTime;
        const progress = Math.min(elapsed / duration, 1);

        const eased = 1 - Math.pow(1 - progress, 3);
        const currentScale = startScale + (endScale - startScale) * eased;

        setScale(currentScale);
        stageRef.current.scale({ x: currentScale, y: currentScale });
        stageRef.current.batchDraw();

        if (progress < 1) {
          initialAnimationRef.current = requestAnimationFrame(animate);
        } else {
          isFirstRenderRef.current = false;
          initialAnimationRef.current = null;
          isInitializingRef.current = false;
          isAnimatingRef.current = false;
          setIsInitialZooming(false);
          handleAnimationEnd();
        }
      };

      initialAnimationRef.current = requestAnimationFrame(animate);
    }, pauseDuration);
  }, [setScale, handleAnimationEnd, isAnimatingRef, stageRef]);

  // Add cleanup for the animation
  useEffect(() => {
    return () => {
      if (initialAnimationRef.current) {
        cancelAnimationFrame(initialAnimationRef.current);
        initialAnimationRef.current = null;
      }

    };
  }, []);

  //INITIALIZATION FLOW
  //First get users location, then get posters, then intialize the app
  useEffect(() => {
    if (!isInitialized && Object.keys(posters).length > 0) {
      (async () => {
        try {
          const urlPosterId = searchParams.get('posterId');

          if (!posters || Object.keys(posters).length === 0) {
            console.log('Waiting for posters data...');
            return;
          }
          console.log(posters.length);
          console.log('Posters from context: ', posters);
          posterCount.current = posters.length;

          pixelsPerHundredFeet.current = calculatePixelsPerHundredFeet(posters, location.latitude, location.longitude);
          pixelsPerMile.current = Math.abs(pixelsPerHundredFeet.current * 52.80);

          const transformedPosters = posters.map(poster => {
            const canvasCoords = gpsToCanvasCoords(
              poster.latitude,
              poster.longitude,
              location.latitude,
              location.longitude,
              pixelsPerHundredFeet.current
            );

            return {
              ...poster,
              x: canvasCoords.x,
              y: canvasCoords.y,
              distance: canvasCoords.distance
            };
          });

          const distributedPosters = distributePosters(transformedPosters, location.latitude, location.longitude);

          //When in ShareMode, find shared poster
          const sharedPoster = urlPosterId
            ? distributedPosters.find(poster => poster.id === urlPosterId)
            : null;

          //create Welcome Poster
          const welcomePoster = {
            id: 'welcomeposter',
            image_url: '/data/images/welcome-poster-new.png',
            x_new: -125,
            y_new: -175,
            fastener_type: '/data/images/pins/star_pin.png',
            zIndex: 49
          };


          //when in share mode, swap the welcome poster position with the shared poster
          if (isShareMode && sharedPoster) {
            const welcomePosterOriginalX = welcomePoster.x_new;
            const welcomePosterOriginalY = welcomePoster.y_new;
            const sharedPosterOriginalX = sharedPoster.x_new;
            const sharedPosterOriginalY = sharedPoster.y_new;


            welcomePoster.x_new = sharedPosterOriginalX;
            welcomePoster.y_new = sharedPosterOriginalY;
            welcomePoster.zIndex = 0;

            sharedPoster.x_new = welcomePosterOriginalX;
            sharedPoster.y_new = welcomePosterOriginalY;
            sharedPoster.zIndex = 49;


            setPosters([
              ...(distributedPosters.filter(poster => poster.id !== urlPosterId)),
              welcomePoster,
              sharedPoster
            ]);

          } else {
            setPosters([
              ...distributedPosters,
              welcomePoster
            ]);
          }

          setLoading(false);
          requestAnimationFrame(() => {
            isDomReadyRef.current = true;
          });
        } catch (err) {
          setError(err.message);
          setLoading(false);
        }
      })();
      setIsInitialized(true)
    } else if (!isLocationSet) {
      initializeLocation();
    } else if (!isGetPosterRequestSent) {
      initializePosters();
    }
  }, [zipcode, location, posters, isInitialized, isLocationSet, isGetPosterRequestSent, isShareMode, performInitialZoomAnimation, searchParams, setError, setLoading, setPosters, initializeLocation, initializePosters])

  useEffect(() => {
    if (postersLoadedCount > (posterCount.current * .75) && isDomReadyRef.current && isFirstRenderRef.current) {
      performInitialZoomAnimation();
    }
  },[postersLoadedCount, performInitialZoomAnimation])


  // Add cleanup to component unmount
  useEffect(() => {
    const currentZoomEndTimeoutRef = zoomEndTimeoutRef.current;
    const currentInertiaAnimation = inertiaAnimationRef.current;
    return () => {
      if (currentZoomEndTimeoutRef) {
        clearTimeout(currentZoomEndTimeoutRef);
      }
      if (currentInertiaAnimation) {
        cancelAnimationFrame(currentInertiaAnimation);
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      cleanup(); // canvas cleanup
      cleanupGestures(); // gestures cleanup
    };
  }, [cleanup, cleanupGestures]);

  if (loading) {
    return <LocationFetchScreen onZipcodeSubmit={handleZipcodeSubmit} locationFetchMessage={locationFetchMessage} />;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <>
      <SubmitPosterModal
        isOpen={showHelp}
        onClose={() => setShowHelp(!showHelp)}
        userLocation={location}
      />

      <Stage
        ref={stageRef}
        width={stageSize.width}
        height={stageSize.height}
        x={position.x}
        y={position.y}
        onWheel={handleWheel}
        onTouchMove={handleTouchMove}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onClick={handleStageClick}
        onTap={handleStageClick}
        draggable={!isAnimatingRef.current}
        scaleX={scaleRef.current}
        scaleY={scaleRef.current}
      >
        <Layer>
          <Group>
            <Background
              imageSrc={'/data/images/background/cork-background-tile.webp'}
              stagePos={position}
              stageSize={stageSize}
            />
          </Group>
        </Layer>
        <DistanceCircles
          pixelsPerMile={pixelsPerMile.current}
        />
        <Layer>
          {/* Regular posters */}
          {posters.map((poster) => (
            <Poster
              key={poster.id}
              id={poster.id}
              src={poster.image_url}
              x={poster.x_new}
              y={poster.y_new}
              reaction_count_1={poster.reaction_count_1}
              reaction_count_2={poster.reaction_count_2}
              reaction_count_3={poster.reaction_count_3}
              reaction_count_4={poster.reaction_count_4}
              fastenerSrc={poster.fastener_type}
              onPosterTouchStart={handlePosterTouchStart}
              onPosterTouchEnd={handlePosterTouchEnd}
              zIndex={lastTouchedPosterId === poster.id ? highestZIndex : poster.zIndex || 0}
              lastTouchedPosterId={lastTouchedPosterId}
              onPosterLoaded={onPosterLoaded}
              latitude={poster.latitude}
              longitude={poster.longitude}
              eventUrl={poster.event_url}
            />
          ))}
        </Layer>
      </Stage>
      {isInitialZooming && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          pointerEvents: 'none',
          zIndex: 1000,
          transition: 'opacity 0.3s ease-out',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            color: 'white',
            fontSize: '24px',
            fontWeight: '500',
            textAlign: 'center',
            textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}>
            Loading your corkboard...
          </div>
        </div>
      )}
    </>
  );
};
export default InfiniteCanvas;

